import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminChangePasswordRequest,
  AdminCreateAdminRequest,
} from '@naya/shared'
import { adminApi } from '../adminApi.js'
import type {
  AccountResponse,
  AdminListResponse,
  AdminRow,
} from '../types.js'
import { adminMeKey } from './useAdminMe.js'

export const adminsListKey = ['admin', 'admins', 'list'] as const
export const accountKey = ['admin', 'account'] as const

interface AdminResponse {
  admin: AdminRow
}

export function useAdminsList() {
  return useQuery({
    queryKey: adminsListKey,
    queryFn: () => adminApi.get<AdminListResponse>('/admins'),
  })
}

export function useCreateAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminCreateAdminRequest) =>
      adminApi.post<AdminResponse>('/admins', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminsListKey })
    },
  })
}

export function useDeactivateAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (adminId: string) =>
      adminApi.post<AdminResponse>(`/admins/${adminId}/deactivate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminsListKey })
    },
  })
}

export function useReactivateAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (adminId: string) =>
      adminApi.post<AdminResponse>(`/admins/${adminId}/reactivate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminsListKey })
    },
  })
}

/** GET /account — the logged-in admin's own record (email, last login). */
export function useAccount() {
  return useQuery({
    queryKey: accountKey,
    queryFn: () => adminApi.get<AccountResponse>('/account'),
  })
}

export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminChangePasswordRequest) =>
      adminApi.post<{ ok: true }>('/account/change-password', input),
    onSuccess: () => {
      // A password change clears must_change_password — refresh /me + account.
      void qc.invalidateQueries({ queryKey: adminMeKey })
      void qc.invalidateQueries({ queryKey: accountKey })
    },
  })
}
